import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApplicantFilter from '@company/types/applicant-filter.type';
import { CandidateStage } from '@company/types/job-overview.type';
import {
  useJobDetailStore,
  useActiveCandidateFilters,
  defaultCandidateFilters,
} from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';

enum CandidateUrlParam {
  TAB = 'tab',
  SEARCH = 'q',
  GENDER = 'gender',
  COUNTRY = 'country',
  SORT_BY = 'sortBy',
  ORDER = 'order',
}

const MANAGED_PARAMS = Object.values(CandidateUrlParam);

const isValidTab = (value: string | null): value is CandidateStage =>
  !!value && Object.values(CandidateStage).includes(value as CandidateStage);

const splitList = (value: string | null): string[] | undefined => {
  if (!value) return undefined;
  const items = value.split(',').filter(Boolean);
  return items.length ? items : undefined;
};

const managedParamsSignature = (params: URLSearchParams): string =>
  MANAGED_PARAMS.map((key) => `${key}=${params.get(key) ?? ''}`).join('&');

const parseCandidateUrlState = (
  params: URLSearchParams
): { tab: CandidateStage; filters: ApplicantFilter } => {
  const tabParam = params.get(CandidateUrlParam.TAB);
  const tab = isValidTab(tabParam) ? tabParam : CandidateStage.NEW;

  const defaults = defaultCandidateFilters(tab);
  const search = params.get(CandidateUrlParam.SEARCH);
  const sortBy = params.get(CandidateUrlParam.SORT_BY);
  const order = params.get(CandidateUrlParam.ORDER);

  return {
    tab,
    filters: {
      ...defaults,
      nameOrEmail: search ?? '',
      gender: splitList(params.get(CandidateUrlParam.GENDER)),
      country: splitList(params.get(CandidateUrlParam.COUNTRY)),
      sortBy: sortBy ?? defaults.sortBy,
      order: order ?? defaults.order,
    },
  };
};

const serializeCandidateUrlState = (
  tab: CandidateStage,
  filters: ApplicantFilter,
  base: URLSearchParams
): URLSearchParams => {
  const params = new URLSearchParams(base);
  MANAGED_PARAMS.forEach((key) => params.delete(key));

  const defaults = defaultCandidateFilters(tab);

  if (tab !== CandidateStage.NEW) {
    params.set(CandidateUrlParam.TAB, tab);
  }
  if (filters.nameOrEmail) {
    params.set(CandidateUrlParam.SEARCH, filters.nameOrEmail);
  }
  if (filters.gender?.length) {
    params.set(CandidateUrlParam.GENDER, filters.gender.join(','));
  }
  if (filters.country?.length) {
    params.set(CandidateUrlParam.COUNTRY, filters.country.join(','));
  }
  if (filters.sortBy && filters.sortBy !== defaults.sortBy) {
    params.set(CandidateUrlParam.SORT_BY, filters.sortBy);
  }
  if (filters.order && filters.order !== defaults.order) {
    params.set(CandidateUrlParam.ORDER, filters.order);
  }

  return params;
};

/**
 * Centralized hook to keep the active candidate tab + filters in sync with the URL.
 */
export default function useCandidateFiltersUrlSync(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useJobDetailStore((state) => state.activeTab);
  const setActiveTab = useJobDetailStore((state) => state.setActiveTab);
  const setCandidateFilters = useJobDetailStore(
    (state) => state.setCandidateFilters
  );
  const filters = useActiveCandidateFilters();

  const lastSyncedRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    // Wait for useJobDetailUrlSync to resolve the worker's tab.
    if (searchParams.get('openConvo') === 'true') return;

    const signature = managedParamsSignature(searchParams);
    if (signature === lastSyncedRef.current) return;
    lastSyncedRef.current = signature;

    const { tab, filters: urlFilters } = parseCandidateUrlState(searchParams);
    setActiveTab(tab);
    setCandidateFilters(urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('openConvo') === 'true') return;

    const state = useJobDetailStore.getState();
    const current = state.candidateFiltersByTab[state.activeTab];
    const next = serializeCandidateUrlState(
      state.activeTab,
      current,
      searchParams
    );
    const nextSignature = managedParamsSignature(next);
    if (nextSignature !== managedParamsSignature(searchParams)) {
      lastSyncedRef.current = nextSignature;
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters, searchParams]);
}
